import { ApiService } from "@core/api/api-service";
import { ProgramContent } from "@domain/program/program";
import {
	getProgramContentFromDto,
	getProgramLightContentDtoFromProgramContent,
	ProgramContentDto,
} from "@domain/program/program-content-dto";
import { Template, TemplateItem, TemplateMetadata } from "@domain/template/template";
import { dateFromDateDto, now } from "@utils/date-utils";

export class TemplateService {
	constructor(private readonly apiService: ApiService) {}

	async loadAllTemplates(): Promise<TemplateItem[]> {
		const result = await this.apiService.get<TemplateItemDto[]>("/templates");
		return result.data.map(template => TemplateService.templateItemFromDto(template));
	}

	async loadTemplate(templateId: string): Promise<Template> {
		const result = await this.apiService.get<TemplateDto>(`/templates/${templateId}`);
		return TemplateService.templateFromDto(result.data);
	}

	async createTemplate(newTemplate: ProgramContent): Promise<TemplateMetadata> {
		const newTemplateDto = getProgramLightContentDtoFromProgramContent(newTemplate);
		const result = await this.apiService.post<TemplateMetadataDto>(
			"/templates",
			newTemplateDto,
			undefined,
			() => false,
		);
		return TemplateService.metadataFromDto(result.data);
	}

	async updateTemplate(templateId: string, draftTemplate: ProgramContent): Promise<TemplateMetadata> {
		const newTemplateDto = getProgramLightContentDtoFromProgramContent(draftTemplate);
		const result = await this.apiService.post<TemplateMetadataDto>(
			`/templates/${templateId}`,
			newTemplateDto,
			undefined,
			() => false,
		);
		return TemplateService.metadataFromDto(result.data);
	}

	async deleteTemplate(templateId: string): Promise<void> {
		await this.apiService.delete(`/templates/${templateId}`);
	}

	async transformProgramToTemplate(
		patientId: string,
		programId: string,
		templateName: string,
	): Promise<TemplateItem> {
		const result = await this.apiService.post<TemplateItemDto>(
			`/patients/${patientId}/programs/${programId}/template`,
			{
				name: templateName,
			},
		);
		return TemplateService.templateItemFromDto(result.data);
	}

	// Dto Conversions
	private static templateFromDto(templateDto: TemplateDto): Template {
		return {
			id: templateDto.id,
			...getProgramContentFromDto(templateDto),
		};
	}

	private static templateItemFromDto(templateLightDto: TemplateItemDto): TemplateItem {
		return {
			...templateLightDto,
			lastEditionDate: dateFromDateDto(templateLightDto.lastEditionDate),
		};
	}

	private static metadataFromDto(metadataDto: TemplateMetadataDto): TemplateMetadata {
		return {
			...metadataDto,
			lastEditionDate: now(),
		};
	}
}

interface TemplateDto extends ProgramContentDto {
	id: string;
}

interface TemplateMetadataDto {
	id: string;
}

interface TemplateItemDto extends Omit<TemplateItem, "lastEditionDate"> {
	lastEditionDate: string;
}
